import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Popconfirm,
  message,
  Typography,
  Row,
  Col,
  Switch
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { User, CreateUserForm } from '../../types'
import { userService, UpdateUserForm, UserFilter } from '../../services/user'
import { UserFormModal, PasswordResetModal } from '../../components/User'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'

const { Title } = Typography
const { Option } = Select

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  
  // 筛选状态
  const [filter, setFilter] = useState<UserFilter>({})
  const [searchUsername, setSearchUsername] = useState('')
  const [searchRole, setSearchRole] = useState<string>('')
  
  // 模态框状态
  const [userModalVisible, setUserModalVisible] = useState(false)
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null)
  const [modalLoading, setModalLoading] = useState(false)

  // 获取当前用户信息，用于权限控制
  const currentUser = useSelector((state: RootState) => state.auth.user)

  // 加载用户列表
  const loadUsers = async () => {
    try {
      setLoading(true)
      const result = await userService.getUsers(filter, { page: currentPage, pageSize })
      setUsers(result.data)
      setTotal(result.total)
    } catch (error) {
      message.error('加载用户列表失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [currentPage, pageSize, filter])

  // 搜索处理
  const handleSearch = () => {
    const newFilter: UserFilter = {}
    if (searchUsername) newFilter.username = searchUsername
    if (searchRole) newFilter.role = searchRole as any
    
    setFilter(newFilter)
    setCurrentPage(1)
  }

  // 重置搜索
  const handleReset = () => {
    setSearchUsername('')
    setSearchRole('')
    setFilter({})
    setCurrentPage(1)
  }

  // 创建用户
  const handleCreateUser = async (userData: CreateUserForm) => {
    try {
      setModalLoading(true)
      await userService.createUser(userData)
      message.success('用户创建成功')
      setUserModalVisible(false)
      loadUsers()
    } catch (error) {
      message.error('用户创建失败')
    } finally {
      setModalLoading(false)
    }
  }

  // 更新用户
  const handleUpdateUser = async (userData: UpdateUserForm) => {
    if (!editingUser) return
    
    try {
      setModalLoading(true)
      await userService.updateUser(editingUser.id, userData)
      message.success('用户更新成功')
      setUserModalVisible(false)
      setEditingUser(null)
      loadUsers()
    } catch (error) {
      message.error('用户更新失败')
    } finally {
      setModalLoading(false)
    }
  }

  // 删除用户
  const handleDeleteUser = async (user: User) => {
    try {
      await userService.deleteUser(user.id)
      message.success('用户删除成功')
      loadUsers()
    } catch (error) {
      message.error('用户删除失败')
    }
  }

  // 重置密码
  const handleResetPassword = async (password: string) => {
    if (!resetPasswordUser) return
    
    try {
      setModalLoading(true)
      await userService.resetPassword(resetPasswordUser.id, password)
      message.success('密码重置成功')
      setPasswordModalVisible(false)
      setResetPasswordUser(null)
    } catch (error) {
      message.error('密码重置失败')
    } finally {
      setModalLoading(false)
    }
  }

  // 切换用户状态
  const handleToggleStatus = async (user: User) => {
    try {
      await userService.toggleUserStatus(user.id)
      message.success(`用户已${user.isActive ? '禁用' : '启用'}`)
      loadUsers()
    } catch (error) {
      message.error('状态切换失败')
    }
  }

  // 角色标签颜色
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'red'
      case 'BUSINESS': return 'blue'
      case 'PACKER': return 'green'
      default: return 'default'
    }
  }

  // 角色中文名
  const getRoleName = (role: string) => {
    switch (role) {
      case 'ADMIN': return '管理员'
      case 'BUSINESS': return '业务员'
      case 'PACKER': return '装箱员'
      default: return role
    }
  }

  // 检查是否可以操作用户（不能操作自己）
  const canOperateUser = (user: User) => {
    return currentUser?.id !== user.id
  }

  // 检查是否有管理员权限
  const isAdmin = currentUser?.role === 'ADMIN'

  const columns: ColumnsType<User> = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (role: string) => (
        <Tag color={getRoleColor(role)}>
          {getRoleName(role)}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean, record: User) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleStatus(record)}
          disabled={!isAdmin || !canOperateUser(record)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, record: User) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingUser(record)
              setUserModalVisible(true)
            }}
            disabled={!isAdmin}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            icon={<KeyOutlined />}
            onClick={() => {
              setResetPasswordUser(record)
              setPasswordModalVisible(true)
            }}
            disabled={!isAdmin || !canOperateUser(record)}
          >
            重置密码
          </Button>
          <Popconfirm
            title="确定要删除这个用户吗？"
            onConfirm={() => handleDeleteUser(record)}
            disabled={!isAdmin || !canOperateUser(record)}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={!isAdmin || !canOperateUser(record)}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Title level={2}>用户管理</Title>
      
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Input
              placeholder="搜索用户名"
              value={searchUsername}
              onChange={(e) => setSearchUsername(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="选择角色"
              value={searchRole}
              onChange={setSearchRole}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="ADMIN">管理员</Option>
              <Option value="BUSINESS">业务员</Option>
              <Option value="PACKER">装箱员</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                搜索
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
              >
                重置
              </Button>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setUserModalVisible(true)}
              disabled={!isAdmin}
            >
              新增用户
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page)
              setPageSize(size || 10)
            },
          }}
        />
      </Card>

      {/* 用户表单模态框 */}
      <UserFormModal
        visible={userModalVisible}
        onCancel={() => {
          setUserModalVisible(false)
          setEditingUser(null)
        }}
        onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
        user={editingUser}
        loading={modalLoading}
      />

      {/* 密码重置模态框 */}
      <PasswordResetModal
        visible={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false)
          setResetPasswordUser(null)
        }}
        onSubmit={handleResetPassword}
        username={resetPasswordUser?.username}
        loading={modalLoading}
      />
    </div>
  )
}

export default UserManagement